import React, { FC, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Select, Input, message, Button } from 'antd';
import { useTranslation } from 'next-i18next';
import Popup from 'antd-mobile/es/components/popup';
import Toast from 'antd-mobile/es/components/toast';
import Cascader from 'antd-mobile/es/components/cascader';
import type { CascaderOption } from 'antd-mobile/es/components/cascader-view';
import SearchBar from 'antd-mobile/es/components/search-bar';
import { debounce } from 'lodash-es';

import {
  getPlaygroundDetailList,
  listPlaygroundProvinceCityOptions,
  type AdminDivisionOptionDto,
} from '../../../api/playgroundApi';
import PageList from '../../../components/PageList';
import PGCard, { type Props as PGCardProps } from '../../../components/PGCard';

import styles from './PlaygroundProgressList.module.scss';
import { BreakPoint, useScreenSize } from '../../../components/ScreenSize';

const PlayGroundItem: any = (item: PGCardProps['pgCardData']) => {
  return (
    <div className={styles['playground-item']}>
      <PGCard pgCardData={item} />
    </div>
  );
};

const PlayGroundProgressList: FC<{ lanType: string; prePageText?: string; nextPageText?: string }> = (props) => {
  const { t } = useTranslation('common');
  const { lanType, prePageText, nextPageText } = props;
  const renderItem = PlayGroundItem;

  const { screen } = useScreenSize();

  // 移动端判定：width < 768px
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    const handleResize = debounce(checkMobile, 30);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  // 筛选状态
  const [provinceOptions, setProvinceOptions] = useState<AdminDivisionOptionDto[]>([]);
  const [cityOptions, setCityOptions] = useState<AdminDivisionOptionDto[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<AdminDivisionOptionDto | null>(null);
  const [selectedCity, setSelectedCity] = useState<AdminDivisionOptionDto | null>(null);
  const [playgroundName, setPlaygroundName] = useState('');
  const [debouncedPlaygroundName, setDebouncedPlaygroundName] = useState('');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  // 操场状态 Switch 选中项（提升到外部以保持地区筛选时不丢失）
  // '0'=全部 '1'=已建成(EXECUTED) '2'=建设中(RESERVE)
  const [selectedStatusIndex, setSelectedStatusIndex] = useState('1');
  // Cascader 相关状态（提前声明，供 handleProvinceChange 引用）
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const cityDataMapRef = React.useRef<Record<string, AdminDivisionOptionDto[]>>({});

  // 挂载时加载省份列表
  useEffect(() => {
    listPlaygroundProvinceCityOptions({ lanType })
      .then((res) => {
        setProvinceOptions(res.data || []);
      })
      .catch(() => {
        message.error(t('playgroundListConfig.loadProvinceFailed'));
      });
  }, [lanType, t]);

  // 防抖搜索名称（300ms 无输入后才触发搜索）
  const debouncedSetName = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedPlaygroundName(value.trim());
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetName.cancel();
    };
  }, [debouncedSetName]);

  // 标记是否处于 IME 组合输入（如拼音输入中），组合期间不触发搜索
  const isComposingRef = useRef(false);

  const handleNameChange = (value: string) => {
    setPlaygroundName(value);
    // 组合输入中（如拼音未选字）不触发防抖搜索
    if (!isComposingRef.current) {
      debouncedSetName(value);
    }
  };

  const handleNameCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleNameCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = false;
    debouncedSetName(e.currentTarget.value);
  };

  // 选省后逐级请求城市列表
  const handleProvinceChange = async (adCode: string | undefined) => {
    if (!adCode) {
      setSelectedProvince(null);
      setSelectedCity(null);
      setCityOptions([]);
      return;
    }
    const province = provinceOptions.find((p) => p.adCode === adCode);
    if (!province) return;
    setSelectedProvince(province);
    setSelectedCity(null);
    setCityOptions([]);
    try {
      // 省市筛选项接口：provinceCode 传省的 adCode（国标编码），不是 id
      const res = await listPlaygroundProvinceCityOptions({ lanType, provinceCode: province.adCode });
      const cityList = res.data || [];
      setCityOptions(cityList);
      // 同步到 H5 Cascader 缓存，避免 PC 切 H5 后城市数据丢失
      cityDataMapRef.current[adCode] = cityList;
      const cities = cityList.map((c) => ({
        label: c.name,
        value: c.adCode,
      }));
      setCascaderOptions((prev) =>
        prev.map((opt) =>
          opt.value === adCode
            ? { ...opt, children: [{ label: t('playgroundListConfig.allCities'), value: 'all' }, ...cities] }
            : opt,
        ),
      );
    } catch {
      message.error(t('playgroundListConfig.loadCityFailed'));
    }
  };

  const handleCityChange = (adCode: string | undefined) => {
    if (!adCode || adCode === 'all') {
      setSelectedCity(null);
      return;
    }
    const city = cityOptions.find((c) => c.adCode === adCode);
    if (city) {
      setSelectedCity(city);
    }
  };

  const handleClearFilter = () => {
    setSelectedProvince(null);
    setSelectedCity(null);
    setCityOptions([]);
    setPlaygroundName('');
    setDebouncedPlaygroundName('');
    setTotalCount(null);
  };

  const onLoadData = useCallback(
    async (page: { pageSize: number; pageNo: number; status?: string }) => {
      const { pageSize, pageNo, status } = page;
      try {
        const {
          data: { pageContent, page: resPage },
        } = await getPlaygroundDetailList({
          pageSize,
          pageNo,
          lanType,
          status: status || undefined,
          // 详情分页接口：provinceCode/cityCode 传内部行政区 id，不是 adCode
          provinceCode: selectedProvince?.id,
          cityCode: selectedCity?.id,
          playgroundName: debouncedPlaygroundName || undefined,
        });
        const total = resPage?.totalCount ?? 0;
        setTotalCount(total);
        return {
          data: pageContent,
          page: {
            pageSize,
            pageNo,
            totalCount: total,
          },
        };
      } catch (err: any) {
        // 业务错误（code 非 0）时优先展示后端 message，否则展示通用失败提示
        const errMsg = err?.message || t('playgroundListConfig.loadListFailed');
        message.error(errMsg);
        return {
          data: [],
          page: {
            pageSize,
            pageNo,
            totalCount: 0,
          },
        };
      }
    },
    [lanType, selectedProvince, selectedCity, debouncedPlaygroundName, t],
  );

  const pageSize = useMemo(() => {
    return screen === BreakPoint.sm ? 8 : 9;
  }, [screen]);

  const hasFilter = selectedProvince || selectedCity || debouncedPlaygroundName;

  // ===== H5 筛选弹窗 =====
  const [filterPopupVisible, setFilterPopupVisible] = useState(false);
  // 弹窗内暂存状态
  const [tempName, setTempName] = useState('');
  const [cascaderVisible, setCascaderVisible] = useState(false);
  const [cascaderValue, setCascaderValue] = useState<string[]>([]);

  // 省份选项变化时，同步构建 cascaderOptions
  useEffect(() => {
    if (provinceOptions.length === 0) return;
    const options: CascaderOption[] = provinceOptions.map((p) => {
      const label = p.name;
      // 如果已缓存该省城市数据，直接填充
      const cachedCities = cityDataMapRef.current[p.adCode];
      if (cachedCities && cachedCities.length > 0) {
        const cities = cachedCities.map((c) => ({
          label: c.name,
          value: c.adCode,
        }));
        return {
          label,
          value: p.adCode,
          children: [{ label: t('playgroundListConfig.allCities'), value: 'all' }, ...cities],
        };
      }
      return { label, value: p.adCode, children: [{ label: t('playgroundListConfig.allCities'), value: 'all' }] };
    });
    setCascaderOptions(options);
  }, [provinceOptions, t]);

  // 同步外部选中状态到 cascaderValue
  useEffect(() => {
    const val: string[] = [];
    if (selectedProvince) val.push(selectedProvince.adCode);
    if (selectedCity) val.push(selectedCity.adCode);
    else if (selectedProvince) val.push('all');
    setCascaderValue(val);
  }, [selectedProvince, selectedCity]);

  // 打开筛选弹窗
  const openFilterPopup = () => {
    setTempName(playgroundName);
    setFilterPopupVisible(true);
  };

  // Cascader 选择省时动态加载城市
  const handleCascaderSelect = async (value: string[]) => {
    if (value.length === 1) {
      const provinceCode = value[0];
      const province = provinceOptions.find((p) => p.adCode === provinceCode);
      if (!province) return;

      // 选中省份后，默认选中"全部"城市
      setCascaderValue([provinceCode, 'all']);

      // 检查是否已经加载过城市
      const currentOption = cascaderOptions.find((o) => o.value === provinceCode);
      const hasLoadedCities =
        currentOption?.children &&
        currentOption.children !== Cascader.optionSkeleton &&
        currentOption.children.length > 1;
      if (hasLoadedCities) return;

      // 设置 loading 骨架屏
      setCascaderOptions((prev) =>
        prev.map((opt) => (opt.value === provinceCode ? { ...opt, children: Cascader.optionSkeleton } : opt)),
      );

      try {
        // 省市筛选项接口：provinceCode 传省的 adCode（国标编码），不是 id
        const res = await listPlaygroundProvinceCityOptions({ lanType, provinceCode: province.adCode });
        const cityList = res.data || [];
        cityDataMapRef.current[provinceCode] = cityList;
        const cities = cityList.map((c) => ({
          label: c.name,
          value: c.adCode,
        }));
        setCascaderOptions((prev) =>
          prev.map((opt) =>
            opt.value === provinceCode
              ? { ...opt, children: [{ label: t('playgroundListConfig.allCities'), value: 'all' }, ...cities] }
              : opt,
          ),
        );
      } catch {
        setCascaderOptions((prev) =>
          prev.map((opt) =>
            opt.value === provinceCode
              ? { ...opt, children: [{ label: t('playgroundListConfig.allCities'), value: 'all' }] }
              : opt,
          ),
        );
        Toast.show({ content: t('playgroundListConfig.loadCityFailed'), position: 'bottom' });
      }
    }
  };

  // Cascader 确认选择
  const handleCascaderConfirm = (value: string[]) => {
    setCascaderValue(value);
    setCascaderVisible(false);
  };

  // 获取 Cascader 当前选中文本
  const getCascaderLabel = () => {
    if (cascaderValue.length === 0) return t('playgroundListConfig.selectRegion');
    const province = provinceOptions.find((p) => p.adCode === cascaderValue[0]);
    if (!province) return t('playgroundListConfig.selectRegion');
    if (cascaderValue.length >= 2 && cascaderValue[1] !== 'all') {
      const cityList = cityDataMapRef.current[cascaderValue[0]] || [];
      const city = cityList.find((c) => c.adCode === cascaderValue[1]);
      return `${province.name} · ${city?.name || ''}`;
    }
    return province.name;
  };

  // 点击完成 → 应用筛选
  const handleFilterConfirm = () => {
    const trimmedName = tempName.trim();
    setPlaygroundName(trimmedName);
    setDebouncedPlaygroundName(trimmedName);
    // 从 cascaderValue 解析省市
    if (cascaderValue.length === 0) {
      setSelectedProvince(null);
      setSelectedCity(null);
      setCityOptions([]);
    } else {
      const province = provinceOptions.find((p) => p.adCode === cascaderValue[0]);
      if (province) {
        setSelectedProvince(province);
        const cityList = cityDataMapRef.current[cascaderValue[0]] || [];
        setCityOptions(cityList);
        if (cascaderValue.length >= 2 && cascaderValue[1] !== 'all') {
          const city = cityList.find((c) => c.adCode === cascaderValue[1]);
          setSelectedCity(city || null);
        } else {
          setSelectedCity(null);
        }
      } else {
        setSelectedProvince(null);
        setSelectedCity(null);
        setCityOptions([]);
      }
    }
    setFilterPopupVisible(false);
  };

  // 弹窗内重置
  const handleFilterReset = () => {
    setTempName('');
    setCascaderValue([]);
  };

  // H5 筛选按钮（放在 Switch 右侧）
  const renderMobileFilterBtn = () => (
    <button
      className={`${styles['h5-filter-btn']} ${hasFilter ? styles['h5-filter-btn-active'] : ''}`}
      onClick={openFilterPopup}
      type="button"
    >
      <svg className={styles['h5-filter-btn-icon']} viewBox="0 0 16 16" fill="none">
        <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <span>{t('playgroundListConfig.filter')}</span>
      {hasFilter && <span className={styles['h5-filter-dot']} />}
    </button>
  );

  // H5 底部弹窗内容
  const renderMobileFilterPopup = () => (
    <Popup
      visible={filterPopupVisible}
      onMaskClick={() => setFilterPopupVisible(false)}
      bodyStyle={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}
    >
      <div className={styles['h5-popup-content']}>
        {/* 弹窗头部 */}
        <div className={styles['h5-popup-header']}>
          <span className={styles['h5-popup-title']}>{t('playgroundListConfig.filterTitle')}</span>
          <button className={styles['h5-popup-close']} onClick={() => setFilterPopupVisible(false)} type="button">
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M1 1l12 12M13 1L1 13" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* 搜索输入 */}
        <div className={styles['h5-popup-section']}>
          <label className={styles['h5-popup-label']}>{t('playgroundListConfig.pgNameLabel')}</label>
          <SearchBar
            placeholder={t('playgroundListConfig.searchPlaceholder')}
            value={tempName}
            onChange={(val) => setTempName(val)}
            onClear={() => setTempName('')}
            className={styles['h5-popup-search-bar']}
            maxLength={50}
          />
          {tempName.length > 0 && <div className={styles['input-count']}>{tempName.length}/50</div>}
        </div>

        {/* 地区选择：antd-mobile Cascader */}
        <div className={styles['h5-popup-section']}>
          <label className={styles['h5-popup-label']}>{t('playgroundListConfig.selectRegionLabel')}</label>
          <div
            className={`${styles['h5-cascader-trigger']} ${
              cascaderValue.length > 0 ? styles['h5-cascader-trigger-active'] : ''
            }`}
            onClick={() => setCascaderVisible(true)}
          >
            <span className={styles['h5-cascader-trigger-text']}>{getCascaderLabel()}</span>
            <svg className={styles['h5-cascader-trigger-arrow']} viewBox="0 0 16 16" fill="currentColor">
              <path d="M4.646 5.646a.5.5 0 0 1 .708 0L8 8.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z" />
            </svg>
          </div>
          <Cascader
            options={cascaderOptions}
            visible={cascaderVisible}
            onClose={() => setCascaderVisible(false)}
            value={cascaderValue}
            onConfirm={handleCascaderConfirm}
            onSelect={handleCascaderSelect}
            title={t('playgroundListConfig.selectRegion')}
            cancelText={t('playgroundListConfig.cancel')}
            confirmText={t('playgroundListConfig.confirm')}
          />
        </div>

        {/* 底部按钮 */}
        <div className={styles['h5-popup-footer']}>
          <button className={styles['h5-popup-reset']} onClick={handleFilterReset} type="button">
            {t('playgroundListConfig.reset')}
          </button>
          <button className={styles['h5-popup-confirm']} onClick={handleFilterConfirm} type="button">
            {t('playgroundListConfig.done')}
          </button>
        </div>
      </div>
    </Popup>
  );

  // PC 筛选栏（内联，与 Switch 同行）
  const renderPCFilter = () => (
    <div
      className={styles['filter-controls-pc']}
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
    >
      <Select
        placeholder={t('playgroundListConfig.allProvinces')}
        value={selectedProvince?.adCode}
        onChange={handleProvinceChange}
        size="middle"
        className={styles['filter-select-pc']}
        allowClear
      >
        {provinceOptions.map((p) => (
          <Select.Option key={p.adCode} value={p.adCode}>
            {p.name}
          </Select.Option>
        ))}
      </Select>
      {selectedProvince && (
        <Select
          placeholder={t('playgroundListConfig.allCities')}
          value={selectedCity?.adCode || 'all'}
          onChange={handleCityChange}
          size="middle"
          className={styles['filter-select-pc']}
          allowClear
        >
          <Select.Option value="all">{t('playgroundListConfig.allCities')}</Select.Option>
          {cityOptions.map((c) => (
            <Select.Option key={c.adCode} value={c.adCode}>
              {c.name}
            </Select.Option>
          ))}
        </Select>
      )}
      <Input
        placeholder={t('playgroundListConfig.searchPlaceholderPC')}
        value={playgroundName}
        onChange={(e) => handleNameChange(e.target.value)}
        onCompositionStart={handleNameCompositionStart}
        onCompositionEnd={handleNameCompositionEnd}
        allowClear
        size="middle"
        maxLength={50}
        showCount
        prefix={
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10 10l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        }
        className={styles['filter-input-pc']}
      />
      {hasFilter && totalCount !== null && (
        <span className={styles['filter-result']}>
          {(t('playgroundListConfig.foundCount') as string).replace('{{count}}', String(totalCount))}
        </span>
      )}
      {hasFilter && (
        <Button size="small" className={styles['clear-btn-pc']} onClick={handleClearFilter}>
          {t('playgroundListConfig.clearFilter')}
        </Button>
      )}
    </div>
  );

  // 筛选汇总提示文案（始终展示总数，有筛选条件时附带筛选信息）
  const filterSummaryText = useMemo(() => {
    if (totalCount === null) return null;
    const parts: string[] = [];
    if (selectedProvince) parts.push(selectedProvince.name);
    if (selectedCity) parts.push(selectedCity.name);
    if (debouncedPlaygroundName) parts.push(`"${debouncedPlaygroundName}"`);
    if (parts.length > 0) {
      return (t('playgroundListConfig.summaryWithFilter') as string)
        .replace('{{count}}', String(totalCount))
        .replace('{{location}}', parts.join(' · '));
    }
    return (t('playgroundListConfig.summaryNoFilter') as string).replace('{{count}}', String(totalCount));
  }, [t, totalCount, selectedProvince, selectedCity, debouncedPlaygroundName]);

  const footerNode = filterSummaryText ? <div className={styles['filter-summary']}>{filterSummaryText}</div> : null;

  return (
    <div className={styles['playground-progress-list']}>
      <PageList
        key={`${pageSize}_${selectedProvince?.adCode}_${selectedCity?.adCode}_${debouncedPlaygroundName}`}
        defaultPageSize={pageSize}
        prePageText={prePageText}
        nextPageText={nextPageText}
        renderItem={renderItem}
        onLoadData={onLoadData}
        footerExtra={footerNode}
        switchExtra={isMobile ? renderMobileFilterBtn() : renderPCFilter()}
        defaultSelectedIndex={selectedStatusIndex}
        onSelectedIndexChange={setSelectedStatusIndex}
      />
      {isMobile && renderMobileFilterPopup()}
    </div>
  );
};

export default PlayGroundProgressList;
